import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/app/providers";
import { PushPermissionPrompt, useServiceWorkerUpdate, useWebPushRegistration } from "@/shared/push";
import { wsClient, type WsEventHandler } from "./WebSocketClient";
import { WS_EVENTS, type WsEventName } from "./constants";

type WebSocketContextValue = {
  connected: boolean;
  connectionInfo: Record<string, unknown> | null;
  lastError: string | null;
  on: (event: WsEventName | string, handler: WsEventHandler) => () => void;
  send: (event: WsEventName | string, payload?: Record<string, unknown>) => boolean;
};

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { token, currentBuilding } = useAuth();
  useWebPushRegistration();
  useServiceWorkerUpdate();
  const [connected, setConnected] = useState(() => wsClient.isConnected());
  const [connectionInfo, setConnectionInfo] = useState<Record<string, unknown> | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const buildingIdRef = useRef<number | null>(null);

  const buildingId = currentBuilding?.pk ?? null;
  buildingIdRef.current = buildingId;

  useEffect(() => {
    if (!token) {
      wsClient.disconnect();
      setConnected(false);
      setConnectionInfo(null);
      return;
    }

    setLastError(null);
    wsClient.connect({
      token,
      buildingId,
      client: "accueilv2",
      onConnectionChange: setConnected,
      onError: setLastError,
    });
    if (wsClient.isConnected()) {
      setConnected(true);
    }

    // Ne pas disconnect au démontage (StrictMode / HMR) — évite ECONNRESET sur le proxy Vite.
    return undefined;
  }, [token]);

  // Re-envoie connectionInit quand le bâtiment change
  useEffect(() => {
    if (!token || !connected) return;
    wsClient.initConnection(buildingId);
  }, [token, buildingId, connected]);

  useEffect(() => {
    const unsubs = [
      wsClient.on(WS_EVENTS.CONNECTION_INIT, (payload) => {
        setConnectionInfo(payload);
        setLastError(null);
      }),
      wsClient.on(WS_EVENTS.ERROR, (payload) => {
        const message =
          typeof payload.message === "string" ? payload.message : "Erreur WebSocket";
        setLastError(message);
      }),
      // Dispatch DOM events pour découpler les composants
      wsClient.on(WS_EVENTS.MESSAGING_MESSAGE_RECEIVE, (payload) => {
        window.dispatchEvent(
          new CustomEvent("ws:messaging.messageReceive", { detail: payload }),
        );
        // Compat avec l'ancien event name utilisé par useMessagingUnread
        window.dispatchEvent(
          new CustomEvent("messaging:messageReceive", { detail: payload }),
        );
      }),
      wsClient.on(WS_EVENTS.MESSAGING_MESSAGE_SENT, (payload) => {
        window.dispatchEvent(
          new CustomEvent("ws:messaging.messageSent", { detail: payload }),
        );
        window.dispatchEvent(
          new CustomEvent("messaging:messageSent", { detail: payload }),
        );
      }),
      wsClient.on(WS_EVENTS.MESSAGING_MESSAGE_READ, (payload) => {
        window.dispatchEvent(
          new CustomEvent("ws:messaging.messageRead", { detail: payload }),
        );
      }),
      wsClient.on(WS_EVENTS.MESSAGING_CONVERSATION_CLOSED, (payload) => {
        window.dispatchEvent(
          new CustomEvent("ws:messaging.conversationClosed", { detail: payload }),
        );
      }),
      // Ticketing
      ...[
        WS_EVENTS.TICKETING_TICKET_CREATED,
        WS_EVENTS.TICKETING_TICKET_UPDATED,
        WS_EVENTS.TICKETING_TICKET_ASSIGNED,
        WS_EVENTS.TICKETING_TICKET_MERGED,
        WS_EVENTS.TICKETING_TICKET_RESOLVED,
        WS_EVENTS.TICKETING_TICKET_DELETED,
      ].map((ev) =>
        wsClient.on(ev, (payload) =>
          window.dispatchEvent(new CustomEvent(`ws:${ev}`, { detail: payload })),
        ),
      ),
      // Cart / CartBar
      ...[
        WS_EVENTS.CART_ORDER_CREATED,
        WS_EVENTS.CART_ORDER_STATUS_CHANGED,
        WS_EVENTS.CART_BAR_ORDER_CREATED,
        WS_EVENTS.CART_BAR_ORDER_STATUS_CHANGED,
      ].map((ev) =>
        wsClient.on(ev, (payload) =>
          window.dispatchEvent(new CustomEvent(`ws:${ev}`, { detail: payload })),
        ),
      ),
      // Colis / Services / List / Salle
      ...[
        WS_EVENTS.COLIS_PARCEL_CREATED,
        WS_EVENTS.COLIS_PARCEL_RECEIVED,
        WS_EVENTS.COLIS_PARCEL_READY,
        WS_EVENTS.COLIS_PARCEL_COLLECTED,
        WS_EVENTS.COLIS_PARCEL_DELETED,
        WS_EVENTS.SERVICES_RESERVATION_CREATED,
        WS_EVENTS.SERVICES_RESERVATION_CANCELLED,
        WS_EVENTS.SERVICES_RESERVATION_UPDATED,
        WS_EVENTS.SERVICES_RESERVATION_REMINDER,
        WS_EVENTS.SERVICES_SLOT_CHANGED,
        WS_EVENTS.LIST_APPOINTMENT_ARRIVED,
        WS_EVENTS.LIST_APPOINTMENT_DELETED,
        WS_EVENTS.LIST_UPDATED,
        WS_EVENTS.SALLE_RESERVATION_CREATED,
        WS_EVENTS.SALLE_RESERVATION_UPDATED,
        WS_EVENTS.SALLE_RESERVATION_DELETED,
      ].map((ev) =>
        wsClient.on(ev, (payload) =>
          window.dispatchEvent(new CustomEvent(`ws:${ev}`, { detail: payload })),
        ),
      ),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const on = useCallback(
    (event: WsEventName | string, handler: WsEventHandler) => wsClient.on(event, handler),
    [],
  );

  const send = useCallback(
    (event: WsEventName | string, payload: Record<string, unknown> = {}) =>
      wsClient.send(event, payload),
    [],
  );

  const value = useMemo(
    () => ({ connected, connectionInfo, lastError, on, send }),
    [connected, connectionInfo, lastError, on, send],
  );

  return (
    <WebSocketContext.Provider value={value}>
      <PushPermissionPrompt />
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return ctx;
}
